import { boundsOfPoints, segment, vec, type Bounds, type Shape, type Vec2 } from '../../geom';
import type { ToolContext, ToolEvent } from '../types';
import { BaseTool } from './base';

/** The box's four sides, so it shows on the canvas while the comment is written. */
const sides = ({ min, max }: Bounds): Shape[] => {
  const corners = [min, vec(max.x, min.y), max, vec(min.x, max.y)];
  return corners.map((corner, i) => segment(corner, corners[(i + 1) % 4]!));
};

/**
 * Marks out what a comment is about with a box dragged on the drawing, or a click, which
 * marks the pick tolerance around the point. The box stays until another is drawn or the
 * tool is cancelled, and is the tool's `region`, which the Comments panel posts with.
 */
export class CommentTool extends BaseTool {
  private origin: Vec2 | null = null;
  private box: Bounds | null = null;

  status(): string {
    return this.box
      ? 'Comment: type it in the Comments panel, or drag another box'
      : 'Comment: drag a box around what the comment is about';
  }

  inProgress(): boolean {
    return this.origin !== null;
  }

  override placingPoint(): boolean {
    return false;
  }

  override region(): Bounds | null {
    return this.origin ? null : this.box;
  }

  override previews(): Shape[] {
    return !this.origin && this.box ? sides(this.box) : [];
  }

  override pointerDown(_ctx: ToolContext, event: ToolEvent): void {
    if (event.button !== 0) return;
    this.origin = event.raw;
  }

  override pointerMove(ctx: ToolContext, event: ToolEvent): void {
    if (this.origin) ctx.showMarquee({ start: this.origin, end: event.raw, crossing: true });
  }

  override pointerUp(ctx: ToolContext, event: ToolEvent): void {
    const { origin } = this;
    if (!origin) return;
    this.origin = null;
    ctx.showMarquee(null);
    const pick = ctx.tolerance.pick;
    const dragged = Math.max(Math.abs(event.raw.x - origin.x), Math.abs(event.raw.y - origin.y));
    this.box =
      dragged > pick
        ? boundsOfPoints([origin, event.raw])
        : boundsOfPoints([
            vec(origin.x - pick, origin.y - pick),
            vec(origin.x + pick, origin.y + pick),
          ]);
  }

  override cancel(ctx: ToolContext): void {
    this.origin = null;
    this.box = null;
    ctx.showMarquee(null);
  }
}
